import { EntityLinks, Goal } from '@griply/types';
import { getGoalParentIds } from '@griply/utils';
import {
  TaskListItemTypes,
  TreeItem,
} from 'shared/components/ui/virtualized-tree-list';
import { UpdateGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { InheritColors } from 'shared/types/inherit-colors';
import { goalInboxID } from 'shared/types/orderable-section';

import { ARCHIVED_SECTION_ID, COMPLETED_SECTION_ID } from '../../constants';

export type ResolveProjectGroupMovesOptions = {
  updatedItems: TreeItem[];
  treeItems: Record<ID, TreeItem>;
  projectsMap: Map<ID, Goal>;
  allGoalsMap: Map<ID, Goal>;
  links: EntityLinks;
};

// Re-parenting rides `goalParentChanges`. Sections here are goals, so a drop never starts from
// a life area and never touches one — all this owns is the inherited colour.
export const resolveProjectGroupMoves = ({
  updatedItems,
  treeItems,
  projectsMap,
  allGoalsMap,
  links,
}: ResolveProjectGroupMovesOptions): UpdateGoal[] => {
  const updatedGoals: UpdateGoal[] = [];

  updatedItems.forEach((item) => {
    const project = projectsMap.get(item.id);
    if (item.type !== TaskListItemTypes.Goal || !project) {
      return;
    }

    const parentSection =
      updatedItems.find(({ children }) => children?.includes(item.id)) ??
      Object.values(treeItems).find(({ children }) =>
        children?.includes(item.id),
      );
    if (
      !parentSection ||
      parentSection.id === ARCHIVED_SECTION_ID ||
      parentSection.id === COMPLETED_SECTION_ID
    ) {
      return;
    }

    // Through the gate: a migrated project has no chain, which read as "moved" on a reorder
    // that stayed put and cost the project its life area.
    const [currentParentId] = getGoalParentIds(project, { links });

    if (parentSection.id === goalInboxID) {
      return;
    }

    if (parentSection.id === currentParentId) {
      return;
    }

    if (!allGoalsMap.has(parentSection.id)) {
      return;
    }

    if (project.color === InheritColors.LifeArea) {
      updatedGoals.push({ id: item.id, color: InheritColors.Goal });
    }
  });

  return updatedGoals;
};
